import { useLingui } from '@lingui/react/macro';
import { createFileRoute, Link, useRouter } from '@tanstack/react-router';
import { ArrowLeft, ArrowRight, House } from 'lucide-react';
import { useMemo } from 'react';
import { z } from 'zod';

import { screens } from '@/app/screens';
import { LEGACY_NAV, navAreaOf } from '@/app/shell/navigation';
import { useScreenAccess } from '@/app/shell/screen-access';
import { underConstructionScreen } from '@/app/under-construction.screen';
import { useScreenAgent } from '@/shared/agent';
import { isInternalPath, linkTarget, PageHeader, UnderConstructionState } from '@/shared/patterns';
import { Button } from '@/shared/ui/button';

const search = z.object({ caminho: z.string().max(2048).optional().catch(undefined) });

// Telas que nunca viram atalho "Abrir …": a Início já tem botão próprio, e esta tela não é destino (R5).
const NO_SHORTCUT = new Set(['home', 'underConstruction']);

export const Route = createFileRoute('/_app/em-construcao')({
  validateSearch: search,
  staticData: { screen: underConstructionScreen },
  component: UnderConstruction,
});

function UnderConstruction() {
  const { t, i18n } = useLingui();
  const router = useRouter();
  const { screenAllowed } = useScreenAccess();
  const asked = Route.useSearch().caminho;
  // `caminho` é texto da URL: o que não é caminho interno (`//host`, esquema, `\`) cai no texto genérico.
  const caminho = asked && isInternalPath(asked) ? asked : undefined;
  const area = caminho ? navAreaOf(LEGACY_NAV, caminho) : null;
  // Caminho de uma tela que já migrou: o que falta é uma parte dela (ação, aba), não a tela toda.
  const target = caminho ? screens.byPath(linkTarget(caminho).to) : undefined;
  const migrated = target && !NO_SHORTCUT.has(target.id) && screenAllowed(target) ? target : undefined;
  const exact = area?.exact === true && !target;
  const section = area ? i18n._(area.section.label) : undefined;
  const page = area?.page ? i18n._(area.page.label) : undefined;
  const name = page ?? section ?? '';
  const heading = !area ? t`Esta área está em construção` : exact ? t`${name} está em construção` : t`Esta parte ainda está em construção`;
  const eyebrow = !area ? undefined : exact ? (page ? section : undefined) : page ? `${section ?? ''} › ${page}` : section;

  const context = useMemo(
    () => ({ summary: [eyebrow, heading].filter(Boolean).join(' · '), fields: { area: name || null }, sensitivity: 'public' as const }),
    [eyebrow, heading, name],
  );
  useScreenAgent(underConstructionScreen, context);

  const back = () => {
    if (router.history.canGoBack()) router.history.back();
    else void router.navigate({ to: '/' });
  };

  return (
    <div className="flex flex-col gap-6">
      <PageHeader title={t`Em construção`} />
      <section aria-label={t`Em construção`} className="animate-fade-in rounded-card border border-border bg-card shadow-1">
        <UnderConstructionState
          eyebrow={eyebrow}
          title={heading}
          actions={
            <>
              <Button variant="secondary" onClick={back}>
                <ArrowLeft aria-hidden />
                {t`Voltar`}
              </Button>
              {migrated && caminho ? (
                <Button asChild variant="secondary">
                  <Link {...linkTarget(caminho)}>
                    {t`Abrir ${i18n._(migrated.title)}`}
                    <ArrowRight aria-hidden />
                  </Link>
                </Button>
              ) : null}
              <Button asChild>
                <Link to="/">
                  <House aria-hidden />
                  {t`Ir para o início`}
                </Link>
              </Button>
            </>
          }
        />
      </section>
    </div>
  );
}
